import React, { useRef, useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, Alert, ToggleButtonGroup, ToggleButton, FormControlLabel, Checkbox } from '@mui/material';
import { FormatAlignLeft, FormatAlignCenter, FormatAlignRight } from '@mui/icons-material';

export default function ImageUploadModal({ open, onClose, onInsert }) {
  const fileRef = useRef(null);
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const [alignment, setAlignment] = useState('center');
  const [wrapText, setWrapText] = useState(false);

  // Clean up preview URL on unmount or when file changes
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const resizeImage = (file, maxSizeMB) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions to fit within max file size
          // Start with 90% quality and scale down if needed
          const maxDimension = 2048; // reasonable max dimension
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with quality adjustment
          canvas.toBlob(
            (blob) => {
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(resizedFile);
            },
            'image/jpeg',
            0.85 // 85% quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleLocal = async (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;
    
    setError('');
    
    // Validate file type
    if (!f.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    
    const sizeMB = f.size / (1024 * 1024);
    const MAX_SIZE_MB = 10;
    
    let finalFile = f;
    let wasResized = false;
    
    // If file is too large, resize it
    if (sizeMB > MAX_SIZE_MB) {
      try {
        finalFile = await resizeImage(f, MAX_SIZE_MB);
        wasResized = true;
        const newSizeMB = finalFile.size / (1024 * 1024);
        
        // If still too large after resize, show error
        if (newSizeMB > MAX_SIZE_MB) {
          setError(`Image too large (${newSizeMB.toFixed(1)}MB). Maximum size is ${MAX_SIZE_MB}MB.`);
          return;
        }
      } catch (err) {
        setError('Failed to resize image. Please try a smaller file.');
        return;
      }
    }
    
    setFile(finalFile);
    setFileInfo({
      name: f.name,
      size: (finalFile.size / (1024 * 1024)).toFixed(2) + ' MB',
      wasResized
    });
    
    // Create preview
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    const previewUrl = URL.createObjectURL(finalFile);
    setPreview(previewUrl);
  };

  const handleInsert = () => {
    const trimmedUrl = (url || '').trim();
    if (!file && !trimmedUrl) return;

    // Pass the File (if chosen) AND the URL field. Parent decides upload vs direct URL.
    onInsert({ 
      file, 
      url: trimmedUrl, 
      alt: alt || '',
      alignment,
      wrapText
    });
    
    // Reset on successful insert
    setFile(null);
    setPreview(null);
    setUrl('');
    setAlt('');
    setError('');
    setFileInfo(null);
    setAlignment('center');
    setWrapText(false);
  };

  const handleClose = () => {
    // Clean up
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setFile(null);
    setPreview(null);
    setUrl('');
    setAlt('');
    setError('');
    setFileInfo(null);
    setAlignment('center');
    setWrapText(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Insert Image</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Button 
            variant="outlined" 
            onClick={() => fileRef.current?.click()}
            sx={{
              borderColor: '#D4AF37',
              color: '#D4AF37',
              '&:hover': {
                borderColor: '#F5D76E',
                background: 'rgba(245, 215, 110, 0.12)',
              }
            }}
          >
            Choose File… (max 10MB)
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleLocal}
          />
          
          {fileInfo && (
            <Box sx={{ p: 1, bgcolor: 'rgba(212, 175, 55, 0.1)', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{fileInfo.name}</strong> ({fileInfo.size})
                {fileInfo.wasResized && (
                  <Typography variant="caption" display="block" color="warning.main">
                    ⚠️ Image was automatically resized to meet size limits
                  </Typography>
                )}
              </Typography>
            </Box>
          )}
          
          {preview && (
            <Box 
              sx={{ 
                width: '100%', 
                maxHeight: 300, 
                display: 'flex', 
                justifyContent: 'center',
                border: '2px solid #D4AF37',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#f5f5f5'
              }}
            >
              <img 
                src={preview} 
                alt="Preview" 
                style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
              />
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" textAlign="center">
            — OR —
          </Typography>
          
          <TextField
            label="Image URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            disabled={!!file}
          />
          <TextField
            label="Alt text (optional)"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Describe the image"
          />
          
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Alignment
            </Typography>
            <ToggleButtonGroup
              value={alignment}
              exclusive
              onChange={(e, newAlignment) => {
                if (newAlignment) setAlignment(newAlignment);
              }}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid #D4AF37',
                  color: '#D4AF37',
                  '&.Mui-selected': {
                    background: '#D4AF37',
                    color: '#1A1A1A',
                    '&:hover': {
                      background: '#F5D76E',
                    }
                  },
                  '&:hover': {
                    background: 'rgba(245, 215, 110, 0.12)',
                  }
                }
              }}
            >
              <ToggleButton value="left">
                <FormatAlignLeft sx={{ mr: 0.5 }} />
                Left
              </ToggleButton>
              <ToggleButton value="center">
                <FormatAlignCenter sx={{ mr: 0.5 }} />
                Center
              </ToggleButton>
              <ToggleButton value="right">
                <FormatAlignRight sx={{ mr: 0.5 }} />
                Right
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {(alignment === 'left' || alignment === 'right') && (
            <FormControlLabel
              control={
                <Checkbox 
                  checked={wrapText}
                  onChange={(e) => setWrapText(e.target.checked)}
                  sx={{
                    color: '#D4AF37',
                    '&.Mui-checked': {
                      color: '#D4AF37',
                    }
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  Wrap text around image
                </Typography>
              }
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleInsert}
          disabled={!file && !url.trim()}
          sx={{
            background: '#D4AF37',
            color: '#1A1A1A',
            '&:hover': {
              background: '#F5D76E',
            }
          }}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
}
